import { Component, signal, effect, inject, untracked } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { PageHeader } from '../../../../shared/components/page-header/page-header';
import { ProfilesService } from '../../../../shared/services/profiles.service';
import { MemberCard } from '../../../../shared/components/member-card/member-card';
import { ActivatedRoute } from '@angular/router';
import { SuppliersSidebar } from '../../../../shared/components/suppliers-sidebar/suppliers-sidebar';

@Component({
  selector: 'app-suppliers-page',
  imports: [PageHeader, MemberCard, SuppliersSidebar],
  templateUrl: './suppliers-page.html',
  styleUrl: './suppliers-page.scss',
})
export class SuppliersPage {
  private profilesService = new ProfilesService();
  activatedRoute = inject(ActivatedRoute);
  
  breadcrumbs = [
    { label: 'Home', route: '/' },
    { label: 'Proveedores', isActive: true }
  ];

  suppliers = signal<any[]>([]);
  currentPage = signal(1);
  totalPages = signal(1);
  pageSize = 9;
  isLoading = signal(false);
  supplierType = signal('');
  queryTags = signal<string[]>([]);

  constructor(private titleService: Title, private metaService: Meta) {
    this.titleService.setTitle('Proveedores empresariales en Bolivia| Servicios curados');
    this.metaService.updateTag({ name: 'description', content: 'Encuentra proveedores empresariales en Bolivia previamente curados. Ejecuta soluciones con claridad, respaldo y alineación estratégica.' });
    this.metaService.updateTag({ name: 'keywords', content: 'Proveedores empresariales en Bolivia, servicios empresariales Bolivia, agencias de marketing Bolivia, servicios contables Bolivia, proveedores para empresas Bolivia, outsourcing empresarial Bolivia' });
    this.metaService.updateTag({ property: 'og:title', content: 'Proveedores empresariales en Bolivia| Servicios curados' });
    this.metaService.updateTag({
      property: 'og:description',
      content: 'Encuentra proveedores empresariales en Bolivia previamente curados. Ejecuta soluciones con claridad, respaldo y alineación estratégica.',
    });
    this.metaService.updateTag({ property: 'og:image', content: 'https://web.dev.consultorio.biz/images/SEO/Proveedores.png' });

    this.activatedRoute.params.subscribe((params) => {
      this.supplierType.set(params['type']);
      this.queryTags.set([]); // Clear tags when supplier type changes
    });

    // Read tags from query params (e.g. /suppliers?tag=slug) reactively,
    // so navigating with a tag param filters the listing
    this.activatedRoute.queryParams.subscribe((queryParams) => {
      const tags = Object.values(queryParams)
        .flatMap((value: any) => (Array.isArray(value) ? value : [value]))
        .filter(Boolean);
      this.queryTags.set(tags as string[]);
    });

    // Watch for page, supplier type and query tags changes and reload suppliers
    effect(() => {
      this.currentPage();
      this.supplierType(); // track supplier type changes (route param)
      this.queryTags(); // track tag changes (query param)
      untracked(() => this.loadSuppliers());
    });
  }

  async loadSuppliers(query: string = '', tag: string | null = null) {
    this.isLoading.set(true);
  
    const result = await this.profilesService.getPaginatedProfilesFromApi('supplier', this.supplierType(), this.currentPage(), this.pageSize, query, tag ? [tag] : this.queryTags());
    this.suppliers.set(result.data);
    this.totalPages.set(result.totalPages);

    this.isLoading.set(false);
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.scrollToTop();
    }
  }

  scrollToTop() {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  async handleSearch(query: string) {
    await this.loadSuppliers(query);
  }

  async handleSelectedTag(tag: string) {
    this.queryTags.set([]);
    await this.loadSuppliers('', tag);
  }
}
